import { agentClient, type TransactionData } from '@/services/agent-client'
import { APP } from '@/lib/constants'

// ---------------------------------------------------------------------------
// IndexedDB store interfaces (internal)
// ---------------------------------------------------------------------------

interface PendingRecord {
  id?: number
  transaction: TransactionData
  status: 'pending' | 'retrying'
  timestamp: number
  retry_count: number
  last_error: string | null
}

interface SyncedRecord {
  id?: number
  transaction: TransactionData
  synced_at: number
  agent_transaction_id: string
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DB_NAME = 'borne_interactive'
const DB_VERSION = 2
const STORE_PENDING = 'pending_transactions'
const STORE_SYNCED = 'synced_transactions'
const STORE_PRODUCTS = 'product_cache'
const MAX_RETRIES = 5
const RETRY_DELAYS = APP.RETRY_DELAYS // [1s, 2s, 5s, 10s, 30s]

// ---------------------------------------------------------------------------
// Offline Queue
// ---------------------------------------------------------------------------

class OfflineQueue {
  private db: IDBDatabase | null = null
  private syncInProgress = false
  private syncTimer: ReturnType<typeof setInterval> | null = null
  private onlineHandler: (() => void) | null = null

  // ---- initialization -----------------------------------------------------

  async init(): Promise<void> {
    if (this.db) return

    return new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        if (!db.objectStoreNames.contains(STORE_PENDING)) {
          const pending = db.createObjectStore(STORE_PENDING, {
            keyPath: 'id',
            autoIncrement: true,
          })
          pending.createIndex('status', 'status', { unique: false })
          pending.createIndex('timestamp', 'timestamp', { unique: false })
        }

        if (!db.objectStoreNames.contains(STORE_SYNCED)) {
          const synced = db.createObjectStore(STORE_SYNCED, {
            keyPath: 'id',
            autoIncrement: true,
          })
          synced.createIndex('synced_at', 'synced_at', { unique: false })
        }

        if (!db.objectStoreNames.contains(STORE_PRODUCTS)) {
          db.createObjectStore(STORE_PRODUCTS, { keyPath: 'key' })
        }
      }

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result
        resolve()
      }

      request.onerror = () => reject(request.error)
    })
  }

  // ---- enqueue ------------------------------------------------------------

  async enqueue(transaction: TransactionData): Promise<string> {
    await this.ensureDb()

    const record: PendingRecord = {
      transaction,
      status: 'pending',
      timestamp: Date.now(),
      retry_count: 0,
      last_error: null,
    }

    const id = await this.idbPut<number>(STORE_PENDING, record)

    // Fire-and-forget sync attempt
    this.syncAll().catch(() => {})

    return String(id)
  }

  // ---- sync ---------------------------------------------------------------

  async syncAll(): Promise<void> {
    if (this.syncInProgress || !navigator.onLine) return
    this.syncInProgress = true

    try {
      await this.ensureDb()

      const records = await this.idbGetAll<PendingRecord>(STORE_PENDING)
      const pending = records.filter(
        (r) => r.status === 'pending' || r.status === 'retrying',
      )

      for (const record of pending) {
        await this.syncRecord(record)
      }
    } finally {
      this.syncInProgress = false
    }
  }

  private async syncRecord(record: PendingRecord): Promise<void> {
    try {
      const result = await agentClient.submitTransaction(record.transaction)

      // Success: move to synced store
      const synced: SyncedRecord = {
        transaction: record.transaction,
        synced_at: Date.now(),
        agent_transaction_id: result.transaction_id,
      }

      const tx = this.db!.transaction([STORE_PENDING, STORE_SYNCED], 'readwrite')
      tx.objectStore(STORE_SYNCED).add(synced)
      if (record.id != null) {
        tx.objectStore(STORE_PENDING).delete(record.id)
      }

      await this.idbTxComplete(tx)
    } catch (err) {
      // Failure: update retry count or give up
      record.retry_count += 1
      record.last_error = err instanceof Error ? err.message : String(err)

      if (record.retry_count >= MAX_RETRIES) {
        record.status = 'retrying' // keep for manual inspection
      } else {
        record.status = 'retrying'
        // Wait before next attempt in the loop
        const delay = RETRY_DELAYS[Math.min(record.retry_count - 1, RETRY_DELAYS.length - 1)]
        await new Promise((resolve) => setTimeout(resolve, delay))
      }

      await this.idbPut(STORE_PENDING, record)
    }
  }

  // ---- queries ------------------------------------------------------------

  async getPendingCount(): Promise<number> {
    await this.ensureDb()
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_PENDING, 'readonly')
      const request = tx.objectStore(STORE_PENDING).count()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  // ---- product cache ------------------------------------------------------

  async cacheProducts(products: Record<string, import('@/services/agent-client').Product>): Promise<void> {
    await this.ensureDb()

    const tx = this.db!.transaction(STORE_PRODUCTS, 'readwrite')
    const store = tx.objectStore(STORE_PRODUCTS)

    // Clear existing cache and write fresh entries
    store.clear()
    for (const [key, product] of Object.entries(products)) {
      store.put({ key, ...product })
    }

    await this.idbTxComplete(tx)
  }

  async getCachedProducts(): Promise<Record<string, import('@/services/agent-client').Product> | null> {
    await this.ensureDb()

    const records = await this.idbGetAll<{ key: string } & import('@/services/agent-client').Product>(
      STORE_PRODUCTS,
    )

    if (records.length === 0) return null

    const result: Record<string, import('@/services/agent-client').Product> = {}
    for (const { key, ...product } of records) {
      result[key] = product as import('@/services/agent-client').Product
    }
    return result
  }

  // ---- background sync control --------------------------------------------

  startBackgroundSync(): void {
    this.stopBackgroundSync()

    this.syncTimer = setInterval(() => {
      this.syncAll().catch(() => {})
    }, APP.SYNC_INTERVAL)

    this.onlineHandler = () => {
      this.syncAll().catch(() => {})
    }
    window.addEventListener('online', this.onlineHandler)
  }

  stopBackgroundSync(): void {
    if (this.syncTimer) {
      clearInterval(this.syncTimer)
      this.syncTimer = null
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler)
      this.onlineHandler = null
    }
  }

  // ---- internal IDB helpers -----------------------------------------------

  private async ensureDb(): Promise<void> {
    if (!this.db) await this.init()
  }

  private idbPut<K = number>(storeName: string, record: unknown): Promise<K> {
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeName, 'readwrite')
      const request = tx.objectStore(storeName).put(record)
      request.onsuccess = () => resolve(request.result as K)
      request.onerror = () => reject(request.error)
    })
  }

  private idbGetAll<T>(storeName: string): Promise<T[]> {
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeName, 'readonly')
      const request = tx.objectStore(storeName).getAll()
      request.onsuccess = () => resolve(request.result as T[])
      request.onerror = () => reject(request.error)
    })
  }

  private idbTxComplete(tx: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const offlineQueue = new OfflineQueue()
